import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Typography } from '@otp/ui';
import {
  ToolOutline,
  LearningCenterOutline,
  ChecklistOutline,
  UserOutline,
  CardLink,
  ResourcesContainedOutline,
  FileWarning,
  ChecklistEdit,
  ClockwiseOutline,
} from '@otp/icons';
import { ContentWrapper } from '../common/wrapper/content/wrapper';
import { Actions, AppArea, Subjects } from 'src/graphql/generated/graphql';
import { useAuthContext } from 'src/context/auth.context';
import { getUserPrograms } from 'src/utils/user-data';
import { AdditionalResources } from './additional-resources';
import { useCan } from 'src/utils/can';
import LearningCenter from './learning-center';
import Grid from '@mui/material/Grid';
import { RestaurantMap, RestaurantList } from './restaurant-selection-v2';
import { HomeContainedOutline } from '@otp/icons';
import { ProgramOptIn } from './program-opt-in';
import { ContactAdministrator } from './program-opt-in/contact-administrator';
import { MembersBreakdown } from './manage-otps-v2/members';
import { MembersInOrganisation } from './manage-otps-v2/otp-list/otp-list';
import { Responsive, WidthProvider, Layout } from 'react-grid-layout';
import { TrendsCard } from './trends/trends-card';
import { Link } from 'react-router-dom';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { Routes } from '../common/router/constants';
import { MostRecentCard } from '../common/most/recent';
import { CurrentAssignments } from './wttp/current-assignments';
import { OpenTasks } from './wttp/open-tasks';
import { ResourcesTabsCard } from '../common/resources/ResourcesTabsCard';

const ResponsiveGridLayout = WidthProvider(Responsive);

const layouts = {
  lg: [
    // First row - two equal-sized components
    { i: 'learning', x: 0, y: 0, w: 6, h: 11 },
    { i: 'manageOTPs', x: 6, y: 0, w: 6, h: 11 },

    // Second row - two equal-sized components
    { i: 'restaurants', x: 0, y: 15, w: 6, h: 12 },
    { i: 'most', x: 6, y: 0, w: 6, h: 12 },

    // Third row - Current Assignments and Open Tasks
    { i: 'currentAssignments', x: 0, y: 28, w: 6, h: 11 },
    { i: 'openTasks', x: 6, y: 28, w: 6, h: 11 },
    // { i: 'quickLinks', x: 9, y: 30, w: 3, h: 11 },

    // Fourth row - full width components with consistent heights
    { i: 'additionalResources', x: 0, y: 45, w: 12, h: 6.7 },
    { i: 'resources', x: 0, y: 53, w: 6, h: 11 },
    { i: 'trends', x: 6, y: 53, w: 6, h: 11 },
  ],
  md: [
    { i: 'learning', x: 0, y: 0, w: 12, h: 10 },
    { i: 'manageOTPs', x: 0, y: 10, w: 12, h: 10 },
    { i: 'restaurants', x: 0, y: 20, w: 12, h: 10 },
    { i: 'most', x: 0, y: 30, w: 12, h: 10 },
    { i: 'currentAssignments', x: 0, y: 40, w: 12, h: 11 },
    { i: 'openTasks', x: 0, y: 40, w: 12, h: 11 },
    { i: 'additionalResources', x: 0, y: 60, w: 12, h: 4 },
    { i: 'resources', x: 0, y: 64, w: 12, h: 9 },
  ],
  sm: [
    { i: 'learning', x: 0, y: 0, w: 12, h: 10.5 },
    { i: 'manageOTPs', x: 0, y: 8, w: 12, h: 10.5 },
    { i: 'restaurants', x: 0, y: 16, w: 12, h: 10.5 },
    { i: 'most', x: 0, y: 24, w: 12, h: 10 },
    { i: 'currentAssignments', x: 0, y: 34, w: 12, h: 15 },
    { i: 'openTasks', x: 0, y: 34, w: 12, h: 15 },
    { i: 'additionalResources', x: 0, y: 48, w: 12, h: 7.8 },
    { i: 'resources', x: 0, y: 52, w: 12, h: 11 },
  ],
  xs: [
    { i: 'learning', x: 0, y: 0, w: 12, h: 20 },
    { i: 'manageOTPs', x: 0, y: 7, w: 12, h: 16.5 },
    { i: 'restaurants', x: 0, y: 14, w: 12, h: 17 },
    { i: 'most', x: 0, y: 21, w: 12, h: 12 },
    { i: 'currentAssignments', x: 0, y: 33, w: 12, h: 14 },
    { i: 'openTasks', x: 0, y: 33, w: 12, h: 14 },
    { i: 'additionalResources', x: 0, y: 42, w: 12, h: 9.5 },
    { i: 'resources', x: 0, y: 46, w: 12, h: 18 },
  ],
  xxs: [
    { i: 'learning', x: 0, y: 0, w: 12, h: 16 },
    { i: 'manageOTPs', x: 0, y: 7, w: 12, h: 18 },
    { i: 'restaurants', x: 0, y: 14, w: 12, h: 17 },
    { i: 'most', x: 0, y: 21, w: 12, h: 12 },
    { i: 'currentAssignments', x: 0, y: 33, w: 12, h: 14 },
    { i: 'openTasks', x: 0, y: 33, w: 12, h: 14 },
    { i: 'additionalResources', x: 0, y: 42, w: 12, h: 15.5 },
    { i: 'resources', x: 0, y: 46, w: 12, h: 20 },
  ],
};

export const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const userPrograms = getUserPrograms(user);
  const { can } = useCan();

  const [isDraggable, setIsDraggable] = useState(false);
  const mouseDownPos = useRef({ x: 0, y: 0 });
  const mouseDownTime = useRef(0);
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Track mouse down position and time
    const handleMouseDown = (e: MouseEvent) => {
      mouseDownPos.current = { x: e.clientX, y: e.clientY };
      mouseDownTime.current = Date.now();
      setIsDraggable(false);
    };

    // Check if mouse has moved enough to be a drag vs a click
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggable) {
        const dx = Math.abs(e.clientX - mouseDownPos.current.x);
        const dy = Math.abs(e.clientY - mouseDownPos.current.y);
        const timeDiff = Date.now() - mouseDownTime.current;

        // Only enable dragging if mouse moved more than 10px and after 200ms
        if ((dx > 10 || dy > 10) && timeDiff > 200) {
          setIsDraggable(true);
        }
      }
    };

    // Reset draggable state after mouse up
    const handleMouseUp = () => {
      // We delay turning off draggable to allow the drop to complete
      setTimeout(() => {
        setIsDraggable(false);
      }, 50);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggable]);

  const canReadUsers = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Users,
  });
  const canReadStores = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Stores,
  });
  const canReadIssues = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Issues,
  });

  const canReadCurrentAssignments = can({
    action: Actions.Read,
    appArea: AppArea.Wttp,
    subject: Subjects.Wttp,
  });

  const canReadOpenTasks = can({
    action: Actions.Read,
    appArea: AppArea.Wttp,
    subject: Subjects.Wttp,
  });

  const canReadAdditionalResources = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Common,
  });

  const canReadManageOTPs = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Users,
  });
  const canReadMost = can({
    action: Actions.Read,
    appArea: AppArea.Most,
    subject: Subjects.Most,
  });

  const canReadResources = true;

  const canReadTrends = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Common,
  });

  // Add a permission check for learning center if needed
  const canAccessLearning = can({
    action: Actions.Read,
    appArea: AppArea.Lms,
    subject: Subjects.Curriculums,
  });

  if (
    !canReadUsers &&
    !canReadStores &&
    !canReadIssues &&
    !canReadCurrentAssignments &&
    !canReadOpenTasks &&
    !canReadAdditionalResources &&
    !canReadManageOTPs &&
    !userPrograms.length &&
    !canReadMost &&
    !canReadTrends
  ) {
    return (
      <ContentWrapper>
        <Grid container className="pt-0 md:pt-8">
          <Grid item xs={12}>
            <Card noBorder sx={{ p: 6 }} className="bg-white w-full md:w-auto">
              <Grid container alignItems="center" gap={2}>
                <UserOutline />
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  fontFamily="Inter"
                >
                  {t('my-organisation.program-opt-in.title')}
                </Typography>
              </Grid>

              <Grid container className="mt-4">
                <ContactAdministrator />
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </ContentWrapper>
    );
  }

  if (
    !canReadUsers &&
    !canReadStores &&
    !canReadIssues &&
    !canReadCurrentAssignments &&
    !canReadOpenTasks &&
    !canReadAdditionalResources &&
    !canReadManageOTPs &&
    userPrograms.length &&
    !canReadTrends
  ) {
    return (
      <ContentWrapper>
        <div className="flex gap-2 justify-center w-full pt-0 md:pt-8 flex-col md:flex-row">
          <Card
            noBorder
            sx={{ p: 6 }}
            className="bg-white flex flex-col w-full md:w-auto"
          >
            <div className="flex gap-3 items-center">
              <UserOutline />
              <Typography
                variant="body1"
                fontWeight="medium"
                fontFamily="Inter"
              >
                {t('my-organisation.program-opt-in.title')}
              </Typography>
            </div>

            <div className="flex flex-1 mt-4 gap-2 md:gap-1 flex-col md:flex-row">
              <ProgramOptIn />
            </div>
          </Card>
        </div>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper ref={contentWrapperRef}>
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
        rowHeight={30}
        isResizable={false}
        isDraggable={isDraggable}
        margin={[16, 16]}
        containerPadding={[16, 16]}
        compactType="vertical"
        preventCollision={false}
      >
        {canAccessLearning && (
          <Card
            key="learning"
            noBorder
            sx={{
              p: { xs: 3, sm: 4, md: 6 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
            className="bg-white"
          >
            <Grid
              container
              gap={{ xs: 3, sm: 4, md: 6 }}
              alignItems="flex-start"
              className="h-full"
            >
              <Grid container alignItems="center" gap={2}>
                <LearningCenterOutline />
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  fontFamily="Inter"
                >
                  {t('my-organisation.learning-center.title')}
                </Typography>
              </Grid>
              <Grid container className="flex-1">
                <LearningCenter />
              </Grid>
            </Grid>
          </Card>
        )}

        {canReadManageOTPs && (
          <Card
            key="manageOTPs"
            noBorder
            sx={{
              p: { xs: 3, sm: 4, md: 6 },
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
            className="bg-white"
          >
            <Grid container gap={{ xs: 3, sm: 4, md: 6 }} alignItems="center">
              <Grid container item alignItems="center" gap={2}>
                <ChecklistOutline />
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  fontFamily="Inter"
                >
                  {t('my-organisation.manage-otps-v2.title')}
                </Typography>
              </Grid>
              <Grid container item spacing={{ xs: 2, sm: 3 }}>
                <Grid item xs={12} md={7}>
                  <MembersInOrganisation />
                </Grid>
                <Grid item xs={12} md={5}>
                  <MembersBreakdown />
                </Grid>
              </Grid>
            </Grid>
          </Card>
        )}

        {canReadStores && (
          <Grid key="restaurants" item xs={12}>
            <Card
              noBorder
              sx={{
                p: { xs: 3, sm: 4, md: 6 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              className="bg-white"
              linkPosition="top"
            >
              <Grid container alignItems="center" gap={{ xs: 3, sm: 4, md: 6 }}>
                <Grid container alignItems="center" gap={2}>
                  <HomeContainedOutline />
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    fontFamily="Inter"
                  >
                    {t('my-organisation.restaurant-selection')}
                  </Typography>
                </Grid>
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} md={6}>
                    <RestaurantList />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RestaurantMap />
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        )}

        {canReadMost && (
          <Grid key="most" container item xs={12}>
            <Card
              noBorder
              sx={{
                p: { xs: 3, sm: 4, md: 6 },
              }}
              className="bg-white h-full w-full"
              linkPosition="top"
            >
              <Grid container direction="column" className="h-full p-0">
                <Grid container alignItems="center" gap={2} className="mb-4">
                  <ToolOutline />
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    fontFamily="Inter"
                  >
                    {t('my-organisation.most.title')}
                  </Typography>
                  <Link
                    to={Routes.mostBase.path}
                    className="flex ml-auto gap-2 items-center"
                  >
                    {t('common.go-to-overview')}
                    <CardLink />
                  </Link>
                </Grid>
                <Grid container className="flex-1">
                  <MostRecentCard />
                </Grid>
              </Grid>
            </Card>
          </Grid>
        )}
        {canReadCurrentAssignments && (
          <Grid key="currentAssignments" container item xs={12}>
            <Card
              noBorder
              sx={{ p: { xs: 3, sm: 4, md: 6 } }}
              className="bg-white h-full w-full"
              linkPosition="top"
            >
              <Grid container direction="column" className="h-full p-0">
                <Grid container alignItems="center" gap={2} className="mb-4">
                  <ChecklistEdit />
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    fontFamily="Inter"
                  >
                    {t('my-organisation.wttp.current-assignments.title')}
                  </Typography>
                  <Link
                    to={Routes.wttpQuestionnaireResponses.path}
                    className="flex ml-auto gap-2 items-center"
                  >
                    {t('common.go-to-overview')}
                    <CardLink />
                  </Link>
                </Grid>
                <Grid container className="flex-1">
                  <CurrentAssignments />
                </Grid>
              </Grid>
            </Card>
          </Grid>
        )}
        {canReadOpenTasks && (
          <Grid key="openTasks" container item xs={12}>
            <Card
              noBorder
              sx={{ p: { xs: 3, sm: 4, md: 6 } }}
              className="bg-white h-full w-full"
              linkPosition="top"
            >
              <Grid container direction="column" className="h-full p-0">
                <Grid container alignItems="center" gap={2} className="mb-4">
                  <FileWarning />
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    fontFamily="Inter"
                  >
                    {t('my-organisation.wttp.open-tasks.title')}
                  </Typography>
                  <Link
                    to={Routes.issueTickets.path}
                    className="flex ml-auto gap-2 items-center"
                  >
                    {t('common.go-to-overview')}
                    <CardLink />
                  </Link>
                </Grid>
                <Grid container className="flex-1">
                  <OpenTasks />
                </Grid>
              </Grid>
            </Card>
          </Grid>
        )}
        {canReadAdditionalResources && (
          <Grid key="additionalResources" item xs={12}>
            <Card
              noBorder
              sx={{ p: { xs: 3, sm: 4, md: 6 } }}
              className="bg-white w-full"
              linkPosition="top"
            >
              <Grid container alignItems="center" gap={2}>
                <ToolOutline />
                <Typography fontWeight="medium" fontFamily="Inter">
                  {t('my-organisation.additional-resources.title')}
                </Typography>
              </Grid>
              <Grid container className="mt-4">
                <AdditionalResources />
              </Grid>
            </Card>
          </Grid>
        )}

        {canReadResources && (
          <Grid key="resources" item xs={6}>
            <Card
              noBorder
              sx={{
                p: { xs: 3, sm: 4, md: 6 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              className="bg-white w-full"
            >
              <Typography
                fontWeight="medium"
                fontFamily="Inter"
                className="flex gap-2 items-center"
              >
                <ResourcesContainedOutline />
                {t('my-organisation.resources.title')}
              </Typography>
              <Grid container className="mt-4 flex-1">
                <ResourcesTabsCard />
              </Grid>
            </Card>
          </Grid>
        )}

        {canReadTrends && (
          <Grid key="trends" item xs={6}>
            <Card
              noBorder
              sx={{ p: { xs: 3, sm: 4, md: 6 } }}
              className="bg-white w-full h-full"
            >
              <Grid container alignItems="center" gap={2}>
                <ClockwiseOutline />
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  fontFamily="Inter"
                >
                  {t('my-organisation.trends.title')}
                </Typography>
              </Grid>
              <Grid container className="flex-1 mt-4">
                <TrendsCard />
              </Grid>
            </Card>
          </Grid>
        )}
      </ResponsiveGridLayout>
    </ContentWrapper>
  );
};
